import React, { useEffect, useState } from "react";
import { X, Sparkles, ShieldCheck, Check, Lock, Loader2 } from "lucide-react";
import { toast } from "sonner";
import api from "../lib/api";

const TIER_META = {
  low:  { label: "Everyday",  reveals: ["Contact number (masked digits shown)"] },
  mid:  { label: "Preferred", reveals: ["Full contact reveal", "Family details snapshot"] },
  high: { label: "Premium",   reveals: ["Full contact reveal", "Family details snapshot", "Photo album access", "Kundali PDF included"] },
};

/**
 * MatchBoxModal
 *  props: open, onClose, target: {id, name, photo_initial?}, onUnlocked
 *  Fetches server-derived tier via GET /payments/box-quote?target_profile_id=…
 *  (tier is derived from the TARGET profile's marriage_budget — contract §5).
 *  User cannot override the tier client-side; server is single source of truth.
 */
export default function MatchBoxModal({ open, onClose, target, onUnlocked }) {
  const [quote, setQuote] = useState(null);       // { tier, amount, target_marriage_budget }
  const [loadingQuote, setLoadingQuote] = useState(false);
  const [busy, setBusy] = useState(false);
  const [unlocked, setUnlocked] = useState(null);

  useEffect(() => {
    if (!open || !target?.id) return;
    setUnlocked(null);
    setQuote(null);
    setLoadingQuote(true);
    (async () => {
      try {
        const { data } = await api.get(`/payments/box-quote?target_profile_id=${encodeURIComponent(target.id)}`);
        setQuote(data);
      } catch (err) {
        toast.error(err?.response?.data?.detail || "Could not fetch price");
      } finally { setLoadingQuote(false); }
    })();
  }, [open, target?.id]);

  if (!open) return null;

  const unlock = async () => {
    if (!target?.id) return;
    setBusy(true);
    try {
      // 1) Create Razorpay order — server derives tier from target's marriage_budget
      const { data: order } = await api.post("/payments/create-order", {
        target_profile_id: target.id,
        type: "box",
      });

      // Idempotency: already unlocked earlier
      if (order.already_unlocked) {
        setUnlocked(order);
        toast.info(`Already unlocked at ₹${order.amount} (${order.tier}).`);
        onUnlocked?.(order);
        return;
      }

      // Mock-mode fallback (only when Razorpay keys are absent — preview safety net)
      if (order.gateway === "mock" || !order.key_id) {
        const { data: legacy } = await api.post("/payments/unlock", {
          target_profile_id: target.id, type: "box",
        });
        setUnlocked(legacy);
        toast.success(`Match-Box unlocked · ₹${legacy.amount}`);
        onUnlocked?.(legacy);
        return;
      }

      // 2) Wait for Razorpay Checkout SDK
      const waitForRzp = () => new Promise((resolve, reject) => {
        if (window.Razorpay) return resolve(window.Razorpay);
        let n = 0;
        const t = setInterval(() => {
          if (window.Razorpay) { clearInterval(t); resolve(window.Razorpay); }
          if (++n > 50) { clearInterval(t); reject(new Error("Razorpay SDK failed to load")); }
        }, 100);
      });
      const Razorpay = await waitForRzp();

      // 3) Open Checkout modal
      const options = {
        key: order.key_id,
        amount: order.amount * 100,
        currency: order.currency,
        order_id: order.order_id,
        name: "Vedika Sanskritik Bandhan",
        description: `Match-Box · ${target?.name || "profile"} · ${order.tier} tier`,
        theme: { color: "#7A0E18" },
        handler: async (response) => {
          try {
            const { data: verified } = await api.post("/payments/verify", {
              razorpay_order_id: response.razorpay_order_id,
              razorpay_payment_id: response.razorpay_payment_id,
              razorpay_signature: response.razorpay_signature,
            });
            setUnlocked(verified.unlock);
            toast.success(`Match-Box unlocked · ₹${verified.unlock.amount}`);
            onUnlocked?.(verified.unlock);
          } catch (err) {
            toast.error(err?.response?.data?.detail || "Verification failed");
          }
        },
        modal: { ondismiss: () => toast.info("Payment cancelled") },
      };
      const rzp = new Razorpay(options);
      rzp.open();
    } catch (err) {
      toast.error(err?.response?.data?.detail || err.message || "Unlock failed");
    } finally { setBusy(false); }
  };

  return (
    <div data-testid="matchbox-modal" className="fixed inset-0 z-50 flex items-center justify-center px-4"
      style={{ background: "rgba(24,18,16,0.7)" }}>
      <div className="w-full max-w-2xl" style={{ background: "var(--ivory)", border: "1px solid var(--gold)" }}>
        <div className="flex items-center justify-between px-6 py-4" style={{ borderBottom: "1px solid var(--line)" }}>
          <div className="flex items-center gap-2">
            <Sparkles size={16} style={{ color: "var(--crimson)" }}/>
            <p className="font-serif text-xl" style={{ color: "var(--crimson)" }}>Match-Box · Reveal contact</p>
          </div>
          <button data-testid="mb-close" onClick={onClose} style={{ color: "var(--ash)" }}><X size={18}/></button>
        </div>

        <div className="px-6 py-6">
          <p className="text-sm" style={{ color: "var(--ash)" }}>
            Unlocking contact for <strong style={{ color: "var(--ink)" }}>{target?.name || "this profile"}</strong>.
            The tier is auto-selected from <em>their</em> marriage budget — no client-side override.
          </p>

          {loadingQuote && (
            <div data-testid="mb-quote-loading" className="mt-6 flex items-center gap-2 text-sm" style={{ color: "var(--ash)" }}>
              <Loader2 size={14} className="animate-spin"/> Fetching price…
            </div>
          )}

          {!loadingQuote && quote && (
            <div data-testid={`mb-derived-tier-${quote.tier}`} className="mt-5 editorial-card p-5"
              style={{ borderColor: "var(--crimson)", background: "rgba(122,14,24,0.04)" }}>
              <div className="flex items-start justify-between">
                <div>
                  <p className="text-[10px] tracking-widest uppercase" style={{ color: "var(--ash)" }}>Auto-selected tier</p>
                  <p className="font-serif text-lg mt-1" style={{ color: "var(--crimson)" }}>
                    {(TIER_META[quote.tier] || { label: quote.tier }).label}
                  </p>
                </div>
                <p data-testid="mb-derived-amount" className="text-3xl font-serif" style={{ color: "var(--crimson)" }}>
                  ₹{quote.amount}
                </p>
              </div>
              <ul className="mt-3 space-y-1.5">
                {(TIER_META[quote.tier]?.reveals || []).map((r, i) => (
                  <li key={i} className="text-[11px] inline-flex items-start gap-1.5" style={{ color: "var(--ash)" }}>
                    <Check size={11} style={{ color: "var(--leaf)", marginTop: 2 }}/> {r}
                  </li>
                ))}
              </ul>
              <p className="mt-4 text-[11px]" style={{ color: "var(--ash)" }} data-testid="mb-budget-source">
                Derived from target profile&apos;s marriage budget:{" "}
                <strong style={{ color: "var(--ink)" }}>
                  {quote.target_marriage_budget != null ? `₹${new Intl.NumberFormat("en-IN").format(quote.target_marriage_budget)}` : "not set (default mid)"}
                </strong>
              </p>
            </div>
          )}

          <div className="mt-6 p-3 inline-flex items-start gap-2 text-[11px]" style={{ background: "rgba(200,162,74,0.08)", border: "1px solid var(--gold)", color: "var(--ink)" }}>
            <ShieldCheck size={12} style={{ color: "var(--leaf)", marginTop: 2 }}/>
            Payment via Razorpay Test Mode. Server enforces the tier — client cannot bypass.
          </div>

          {unlocked && (
            <div data-testid="mb-unlocked" className="mt-5 p-4" style={{ background: "rgba(85,127,74,0.05)", border: "1px solid var(--leaf)" }}>
              <p className="font-serif text-lg" style={{ color: "var(--leaf)" }}>Unlocked · ₹{unlocked.amount} ({unlocked.tier})</p>
              <p className="mt-1 text-sm" style={{ color: "var(--ink)" }}>
                Head to the profile detail page to view the revealed contact and family details.
              </p>
            </div>
          )}
        </div>

        <div className="px-6 py-4 flex items-center justify-between" style={{ borderTop: "1px solid var(--line)" }}>
          <button data-testid="mb-cancel" onClick={onClose} className="text-xs uppercase tracking-widest" style={{ color: "var(--ash)" }}>
            Cancel
          </button>
          <button data-testid="mb-unlock" onClick={unlock} disabled={busy || !!unlocked || !quote} className="btn-primary">
            {busy ? "Processing…" : (unlocked ? "Unlocked" : (quote ? `Reveal for ₹${quote.amount}` : "Loading…"))}
            {unlocked ? <Check size={14}/> : <Lock size={14}/>}
          </button>
        </div>
      </div>
    </div>
  );
}
